import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import Modal from '@components/ui/Modal/Modal';
import { Button } from '@components/ui';
import { TextInput } from '@components/ui/Input';
import Select from '@workpay/ds-shared/components/ui/Select';
import { TagsInput } from '@components/ui/TagsInput/TagsInput';
import { useTranslation } from '@hooks/useTranslation';
import { useNotificationStore } from '@stores/notificationStore';
import { uploadFile } from '@services/fileService';
import { createDocument } from '@services/documentVaultService';
import { getDocumentTypes } from '@services/documentTypeService';
import type { DocumentEntityType, DocumentVaultDto } from '@/types/documentVault';
import { WorkpayApiError } from '@lib/apiEnvelope';
import styles from './UploadDocumentModal.module.scss';

const MAX_BYTES = 25 * 1024 * 1024; // 25 MB client-side cap; server ultimately enforces
const ACCEPT = '.pdf,.doc,.docx,image/*';
// Workflow tag for file-management-service. Backend seeds a workflow_config row
// keyed by this name (size/type/scan rules) — see backend/employee-service/docs/api-endpoints.md:645.
const DOCUMENT_VAULT_WORKFLOW = 'DOCUMENT_VAULT';

export interface UploadDocumentScope {
  entityType: DocumentEntityType;
  entityId: string;
  organisationId?: string;
}

export interface UploadDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  scope: UploadDocumentScope;
  onUploaded?: (doc: DocumentVaultDto) => void;
}

function UploadDocumentModal({ isOpen, onClose, scope, onUploaded }: UploadDocumentModalProps) {
  const { t } = useTranslation();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [documentName, setDocumentName] = useState('');
  const [documentTypeId, setDocumentTypeId] = useState<string>('');
  const [tags, setTags] = useState<string[]>([]);
  const [sizeError, setSizeError] = useState('');

  const { data: docTypesPage } = useQuery({
    queryKey: ['documentTypesForUpload'],
    queryFn: () =>
      getDocumentTypes(
        { page: 0, size: 100, sort: 'name,asc' },
        { filters: { status: 'ACTIVE' } },
      ),
    enabled: isOpen,
  });

  const documentTypeOptions = useMemo(
    () => (docTypesPage?.content ?? []).map((dt) => ({ value: dt.id, label: dt.name })),
    [docTypesPage],
  );

  const reset = useCallback(() => {
    setFile(null);
    setDocumentName('');
    setDocumentTypeId('');
    setTags([]);
    setSizeError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const picked = e.target.files?.[0] ?? null;
      if (!picked) return;
      if (picked.size > MAX_BYTES) {
        setSizeError(
          t('K_VALIDATION_FILE_TOO_LARGE', 'File exceeds 25 MB limit. Pick a smaller file.'),
        );
        setFile(null);
        return;
      }
      setSizeError('');
      setFile(picked);
      // Default the document name to the file base name (sans extension).
      if (!documentName) {
        const base = picked.name.replace(/\.[^/.]+$/, '');
        setDocumentName(base);
      }
    },
    [documentName, t],
  );

  const mutation = useMutation({
    mutationFn: async (payload: {
      file: File;
      name: string;
      documentTypeId: string;
      tags: string[];
    }) => {
      // Step 1 — upload bytes to file-management-service. Echo entityType/entityId as
      // metadata for blob-level traceability (backend tolerates them and they help ops
      // trace a blob back to its linked entity without joining through document_vault).
      const uploaded = await uploadFile(payload.file, {
        objectType: scope.entityType,
        objectId: scope.entityId,
        workflow: DOCUMENT_VAULT_WORKFLOW,
      });
      // Step 2 — write the linkage row. Orphan-upload recovery is simpler than a
      // mid-upload rollback, so we don't try to clean up file-mgmt on step-2 failure.
      const doc = await createDocument({
        documentName: payload.name || payload.file.name,
        documentType: 'DOCUMENT',
        fileId: uploaded.id,
        entityType: scope.entityType,
        entityId: scope.entityId,
        organisationId: scope.organisationId,
        documentTypeId: payload.documentTypeId || undefined,
        tags: payload.tags.length > 0 ? payload.tags : undefined,
      });
      return doc;
    },
    onSuccess: (doc) => {
      onUploaded?.(doc);
      handleClose();
    },
    onError: (err: unknown) => {
      const e = err as WorkpayApiError;
      addNotification({
        variant: 'critical',
        title: t(e.description, e.description),
      });
    },
  });

  const handleSubmit = useCallback(() => {
    if (!file) return;
    mutation.mutate({
      file,
      name: documentName.trim(),
      documentTypeId,
      tags,
    });
  }, [file, documentName, documentTypeId, tags, mutation]);

  const canSubmit = !!file && !sizeError && !mutation.isPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('K_MODAL_UPLOAD_DOCUMENT', 'Upload Document')}
      size="sm"
      footer={
        <div className={styles.footer}>
          <Button variant="outline" size="sm" onClick={handleClose} disabled={mutation.isPending}>
            {t('K_CANCEL', 'Cancel')}
          </Button>
          <Button variant="filled" size="sm" onClick={handleSubmit} disabled={!canSubmit}>
            {mutation.isPending
              ? t('K_BUTTON_UPLOADING', 'Uploading...')
              : t('K_DOCS_ACTION_UPLOAD', 'Upload')}
          </Button>
        </div>
      }
    >
      <div className={styles.body}>
        <label className={styles.fileField}>
          <span className={styles.fileLabel}>{t('K_LABEL_FILE', 'File')}</span>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT}
            onChange={handleFileChange}
            className={styles.fileInput}
            aria-label={t('K_LABEL_FILE', 'File')}
          />
          {file && <span className={styles.fileHint}>{file.name}</span>}
          {sizeError && <span className={styles.fileError}>{sizeError}</span>}
        </label>

        <TextInput
          label={t('K_LABEL_DOCUMENT_NAME', 'Document Name')}
          placeholder={t('K_PLACEHOLDER_DOCUMENT_NAME', 'e.g. Offer Letter')}
          value={documentName}
          onChange={(e) => setDocumentName(e.target.value)}
        />

        <Select
          label={t('K_LABEL_DOCUMENT_TYPE', 'Document Type')}
          placeholder={t('K_PLACEHOLDER_DOCUMENT_TYPE', 'Select document type')}
          options={documentTypeOptions}
          value={documentTypeId}
          onChange={setDocumentTypeId}
          searchable
          fullWidth
        />

        <TagsInput
          label={t('K_LABEL_TAGS', 'Tags')}
          placeholder={t('K_PLACEHOLDER_TAGS', 'Type a tag and press Enter')}
          value={tags}
          onChange={setTags}
        />
      </div>
    </Modal>
  );
}

export default UploadDocumentModal;
